import type { Locale } from 'date-fns'
import { bg, da, de, el, enAU, enCA, enGB, enUS, es, et, fr, frCA, it, nl, ptBR } from 'date-fns/locale'

export const localeMap: Record<string, Locale> = {
  'en-GB': enGB,
  'en-US': enUS,
  'en-AU': enAU,
  'en-CA': enCA,
  'en-CY': enGB,
  'en-IE': enGB,
  'fr-CA': frCA,
  'fr-FR': fr,
  'fr-BE': fr,
  'fr-CH': fr,
  'de-DE': de,
  'de-AT': de,
  'de-CH': de,
  'nl-NL': nl,
  'nl-BE': nl,
  'it-IT': it,
  'it-CH': it,
  'es-ES': es,
  'es-MX': es,
  'es-AR': es,
  'es-PE': es,
  'pt-BR': ptBR,
  'da-DK': da,
  'et-EE': et,
  'el-GR': el,
  'el-CY': el,
  'bg-BG': bg,
  // Language-only fallbacks for browsers/OS settings that omit the region code
  'en': enGB,
  'de': de,
  'nl': nl,
  'it': it,
  'es': es,
  'pt': ptBR,
  'da': da,
  'et': et,
  'el': el,
  'bg': bg,
  'fr': fr,
}

// Unrecognised locales fall back to the prefix of their location then fall back to enGB — add new entries to the map above to support additional locales
export const dateFnsLocale: Locale = localeMap[navigator.language] ?? localeMap[navigator.language.split('-')[0]] ?? enGB

const getLocaleDateFormat = (): string => {
  const parts = new Intl.DateTimeFormat(navigator.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(2000, 0, 15)) // Jan 15 — unambiguous

  return parts
    .filter(({ type }) => type !== 'literal')                                         
    .map(({ type }) => {
      switch (type) {
        case 'day': return 'dd'
        case 'month': return 'MM'
        case 'year': return 'yyyy'
        default: return ''
      }
    })
    .join('/')
}

export const localeDateFormat = getLocaleDateFormat()

// Short day/month format without the year (e.g. 'dd/MM' for en-GB, 'MM/dd' for en-US)
export const localeShortDateFormat = localeDateFormat.replace('/yyyy', '').replace('yyyy/', '')

// true when the locale writes day before month (e.g. en-GB 'dd/MM/yyyy').
// Locales that start with 'MM' (e.g. en-US 'MM/dd/yyyy') are month-first.
// Year-first locales (e.g. 'yyyy/MM/dd') fall back to day-first ordinal order.
const dayBeforeMonth = !localeDateFormat.startsWith('MM')

// Ordinal day + short month, in locale order.
// e.g. 'do MMM' → '25th Dec' (en-GB) or 'MMM do' → 'Dec 25th' (en-US)
export const localeOrdinalShortMonthFormat = dayBeforeMonth ? 'do MMM' : 'MMM do'

// Ordinal day + full month, in locale order.
// e.g. 'do MMMM' → '25th December' (en-GB) or 'MMMM do' → 'December 25th' (en-US)
export const localeOrdinalMonthFormat = dayBeforeMonth ? 'do MMMM' : 'MMMM do'
